import {
  ApiReducer,
  makeApiAction,
  makeApiReducer,
  RsaaTypes,
} from '../../../../core/redux/api/reduxStandardApiAction';
import { apiGetMediaHistories } from '../../../../core/redux/api/endpoints';
import { IAppState } from '../../../../core/redux/IAppState';
import { addError, addLoading, getLoading, removeLoadingAndError } from '../../../../core/redux/api/module';
import { ACTION_TYPE } from '../../../../core/redux/actionType';
import { hasTrippedBreaker, updateBreaker } from '../../../../core/redux/api/circuitBreaker/module';
import { HistoricalMedia } from './historicalMedia';
import * as R from 'ramda';
import { assoc } from '../../../../language/module';


export const KEY_ACTION = ACTION_TYPE.MEDIA_HISTORY_REQUEST;
export const ACTIONS    = [
  ACTION_TYPE.MEDIA_HISTORY_REQUEST,
  ACTION_TYPE.MEDIA_HISTORY_RESPONSE,
  ACTION_TYPE.MEDIA_HISTORY_ERROR,
];


/**
 * Constructs an action that can be dispatched to get a guest's media card history: the complete list of printed passes
 * the guest has ever had.
 * @param guestId - the guest whose media history we want to fetch
 */
export const getMediaHistoryAction = (guestId: string) => makeApiAction(
  {
    method:   'GET',
    headers:  (state: IAppState) => ({
      'content-type':  'application/json',
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    endpoint: apiGetMediaHistories(guestId),
    types:    ACTIONS.map(action => ({ type: action, meta: { guestId } })) as RsaaTypes,
    bailout:  (state: IAppState) => !!getLoading(state, guestId, KEY_ACTION) || hasTrippedBreaker(state, KEY_ACTION),
  },
);


const reducer: ApiReducer<HistoricalMedia[]> = (state, action, error, isLoading, payload) => {
  const guestId = action.meta.guestId;

  if (isLoading) {
    return addLoading(state, guestId!, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(withBreaker, guestId!, KEY_ACTION, error);
  }

  const noLoadingOrError = removeLoadingAndError(withBreaker, guestId!, KEY_ACTION);

  // Put the Guest ID on each media object
  const newEntries = (payload || []).map(item => assoc('guestId', guestId, item));

  return assoc(
    'mediaHistories',
    R.concat(
      newEntries,
      R.filter((history: HistoricalMedia) => history.guestId !== guestId, noLoadingOrError.mediaHistories),
    ),
    noLoadingOrError,
  );
};


export const getMediaHistoryReducer = makeApiReducer<HistoricalMedia[]>(ACTIONS, KEY_ACTION, reducer);
